import React, { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import classNames from "classnames";

import { useOnClickOutside } from "hooks/useOnClickOutside";
import { getSavedViews } from "redux/SingleDataset/selectors";
import {
  SAVED_VIEW_PARAMETER,
  VQL_PARAMETER,
} from "helpers/constants/miscellaneous";
import { checkAnyFilterApplied } from "helpers/utility/utilities";

import SavedViewsFilter from "../SavedViewsFilter";
import SaveViewMenu from "../SaveViewMenu";

import styles from "./style.module.scss";

enum SelectedViewType {
  SAVE_VIEW = "SAVE_VIEW",
  SAVED_VIEW_LIST = "SAVED_VIEW_LIST",
}

interface SaveViewOptionsProps {
  onClose: () => void;
  onQueryPanel?: boolean;
}

export const SaveViewOptions = ({
  onClose,
  onQueryPanel,
}: SaveViewOptionsProps) => {
  const [searchParams] = useSearchParams();
  const savedViews = useSelector(getSavedViews);
  const containerRef = useRef<HTMLDivElement>(null);

  const savedViewId = searchParams.get(SAVED_VIEW_PARAMETER);
  const isVQLEnabled = searchParams.get(VQL_PARAMETER);

  const anyFilterApplied = useMemo(
    () => checkAnyFilterApplied(searchParams),
    [searchParams],
  );

  const [selectedView, setSelectedView] = useState<SelectedViewType>(
    savedViews.length < 1 || anyFilterApplied
      ? SelectedViewType.SAVE_VIEW
      : SelectedViewType.SAVED_VIEW_LIST,
  );

  useOnClickOutside(containerRef, onClose);

  const handleStopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleSavedViewClick = () =>
    setSelectedView(SelectedViewType.SAVED_VIEW_LIST);
  const handleSaveAsClick = () => setSelectedView(SelectedViewType.SAVE_VIEW);

  const savedOptionClasses = classNames(styles.savedOption, {
    [styles.active]: selectedView === SelectedViewType.SAVED_VIEW_LIST,
    [styles.hidden]: savedViews.length === 0 || onQueryPanel,
  });

  const saveAsOptionClasses = classNames(styles.saveAsOption, {
    [styles.active]: selectedView === SelectedViewType.SAVE_VIEW,
    [styles.hidden]: (savedViewId && isVQLEnabled) || !anyFilterApplied,
  });

  return (
    <div className={styles.saveViewOptions} ref={containerRef}>
      <div className={styles.optionHeader} onMouseDown={handleStopPropagation}>
        <div className={savedOptionClasses} onClick={handleSavedViewClick}>
          Saved Views
        </div>
        <div className={saveAsOptionClasses} onClick={handleSaveAsClick}>
          Save As New View +
        </div>
      </div>
      <div className={styles.optionBody}>
        {selectedView === SelectedViewType.SAVE_VIEW ? (
          <SaveViewMenu onCloseMenu={onClose} />
        ) : (
          <SavedViewsFilter />
        )}
      </div>
    </div>
  );
};
